/**
 * Test Cases Template Form
 * Test case documentation and tracking
 */
import { useState, useEffect, useRef } from "react";
import { downloadTemplatePDF } from "../utils/pdfGenerator";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Download, Sparkles, RotateCcw, Plus, Trash2 , Save, Clock , FileText} from "lucide-react";
import { RAISection, FollowupsSection, API, useDCTMFile, FileUp, FileDown } from "./shared";
import { useAuth, useSettings } from "../App";

const TestCasesForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { activeSettings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [saving, setSaving] = useState(false);
  const [autoSave, setAutoSave] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(null);
  const autoSaveIntervalRef = useRef(null);
  const dctmFileInputRef = useRef(null);
  
  // Refs to track latest values for auto-save (avoids stale closures)
  const formDataRef = useRef(null);
  const savedTemplateIdRef = useRef(null);
  const editingSavedId = searchParams.get('saved');

  const getEmptyForm = () => ({
    project_name: '', module: '', date: new Date().toISOString().split('T')[0], prepared_by: '', version: '',
    test_cases: [{ tc_id: '', title: '', description: '', preconditions: '', test_steps: '', expected_result: '', actual_result: '', test_data: '', priority: 'Medium', type: 'Functional', status: 'Not Run', executed_by: '', execution_date: '', notes: '' }],
    rai_assignments: [], followups: []
  });

  const getSampleData = () => ({
    project_name: 'E-Commerce Platform v2.0',
    module: 'User Authentication',
    date: new Date().toISOString().split('T')[0],
    prepared_by: 'QA Team',
    version: '2.0',
    test_cases: [
      { tc_id: 'TC-AUTH-001', title: 'Valid user login', description: 'Verify user can login with valid credentials', preconditions: 'User account exists and is active', test_steps: '1. Navigate to login page\n2. Enter valid email\n3. Enter valid password\n4. Click Login button', expected_result: 'User is redirected to dashboard with welcome message', actual_result: 'Pass - User redirected correctly', test_data: 'Email: test@example.com, Password: Test@123', priority: 'Critical', type: 'Functional', status: 'Pass', executed_by: 'Jane Doe', execution_date: '2025-01-15', notes: '' },
      { tc_id: 'TC-AUTH-002', title: 'Invalid password login', description: 'Verify error message for invalid password', preconditions: 'User account exists', test_steps: '1. Navigate to login page\n2. Enter valid email\n3. Enter invalid password\n4. Click Login', expected_result: 'Error message: "Invalid email or password"', actual_result: 'Pass - Correct error shown', test_data: 'Email: test@example.com, Password: wrong123', priority: 'High', type: 'Negative', status: 'Pass', executed_by: 'Jane Doe', execution_date: '2025-01-15', notes: '' },
      { tc_id: 'TC-AUTH-003', title: 'Password reset flow', description: 'Verify password reset email is sent', preconditions: 'User account exists with valid email', test_steps: '1. Click Forgot Password\n2. Enter registered email\n3. Click Submit\n4. Check email inbox', expected_result: 'Reset email received within 2 minutes', actual_result: '', test_data: 'Email: test@example.com', priority: 'High', type: 'Functional', status: 'Not Run', executed_by: '', execution_date: '', notes: 'Requires email server access' },
      { tc_id: 'TC-AUTH-004', title: 'Session timeout', description: 'Verify session expires after 30 min inactivity', preconditions: 'User is logged in', test_steps: '1. Login successfully\n2. Wait 31 minutes without activity\n3. Try to access dashboard', expected_result: 'User is redirected to login page', actual_result: 'Fail - Session did not expire', test_data: '', priority: 'Medium', type: 'Security', status: 'Fail', executed_by: 'Alex Kumar', execution_date: '2025-01-14', notes: 'BUG-006 logged' },
      { tc_id: 'TC-AUTH-005', title: 'Google OAuth login', description: 'Verify user can login with Google account', preconditions: 'Google OAuth configured', test_steps: '1. Click "Sign in with Google"\n2. Select Google account\n3. Authorize app', expected_result: 'User logged in and profile synced', actual_result: '', test_data: 'Test Google account', priority: 'High', type: 'Integration', status: 'Blocked', executed_by: '', execution_date: '', notes: 'Waiting for OAuth credentials' },
      { tc_id: 'TC-AUTH-006', title: 'Account lockout after failures', description: 'Verify account locks after 5 failed attempts', preconditions: 'User account exists', test_steps: '1. Enter valid email\n2. Enter wrong password 5 times\n3. Try correct password', expected_result: 'Account locked message, no login allowed', actual_result: 'Pass - Account locked correctly', test_data: 'Email: test@example.com', priority: 'Critical', type: 'Security', status: 'Pass', executed_by: 'Jane Doe', execution_date: '2025-01-15', notes: '' }
    ],
    rai_assignments: [
      { task: 'Complete regression test suite', responsible: 'QA Engineer', accountable: 'QA Lead', informed: 'Dev Team', due_date: '2025-01-20', status: 'In Progress' }
    ],
    followups: [
      { item: 'Get Google OAuth credentials', owner: 'DevOps', due_date: '2025-01-18', priority: 'High', status: 'Pending' }
    ]
  });

  const [formData, setFormData] = useState(getEmptyForm());
  
  // Keep refs in sync with state
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);
  
  useEffect(() => {
    savedTemplateIdRef.current = savedTemplateId;
  }, [savedTemplateId]);

  const loadSavedTemplate = async (id) => {
    try {
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      const response = await axios.get(`${API}/saved-templates/${id}`, { headers });
      if (response.data && response.data.data) {
        setFormData(response.data.data);
        setSavedTemplateId(id);
      }
    } catch (error) {
      console.error("Error loading saved template:", error);
    }
  };

  // Internal save function that accepts values directly (for use with refs)
  const saveDraftInternal = async (dataToSave, existingSavedId, isAutoSave = false) => {
    if (!isAutoSave) setSaving(true);
    try {
      const draftName = dataToSave.project_name || dataToSave.title || dataToSave.company_name || "Test Cases Draft";
      const payload = {
        name: draftName,
        template_type: "Test Cases",
        template_id: "test-cases",
        format: "DOCX",
        data: dataToSave
      };
      const headers = user?.email ? { 'X-User-Email': user.email } : {};
      
      if (existingSavedId) {
        const response = await axios.put(`${API}/saved-templates/${existingSavedId}`, payload, { headers });
        // Update version if returned from server
        if (response.data?.version) {
          setFormData(prev => ({ ...prev, version: response.data.version }));
        }
      } else {
        const response = await axios.post(`${API}/saved-templates`, payload, { headers });
        if (response.data?.id) {
          setSavedTemplateId(response.data.id);
        }
      }
      setLastSaved(new Date());
      if (!isAutoSave) setSaving(false);
      return true;
    } catch (error) {
      console.error("Error saving draft:", error);
      if (!isAutoSave) setSaving(false);
      return false;
    }
  };

  const handleSaveDraft = async (isAutoSave = false) => {
    const success = await saveDraftInternal(formData, savedTemplateId, isAutoSave);
    if (!isAutoSave) {
      if (success) {
        alert("Draft saved successfully!");
      } else {
        alert("Error saving draft.");
      }
    }
  };

  // Load saved template if editing
  useEffect(() => {
    if (editingSavedId) {
      loadSavedTemplate(editingSavedId);
    }
  }, [editingSavedId]);

  // Auto-save functionality - uses refs to avoid stale closure issues
  useEffect(() => {
    if (autoSave && user?.email) {
      autoSaveIntervalRef.current = setInterval(() => {
        const currentFormData = formDataRef.current;
        const currentSavedId = savedTemplateIdRef.current;
        if (currentFormData) {
          saveDraftInternal(currentFormData, currentSavedId, true);
        }
      }, 30000);
    } else {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    }
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
        autoSaveIntervalRef.current = null;
      }
    };
  }, [autoSave, user?.email]);

  // DCTM Import/Export handlers
  const handleExportDCTM = () => {
    const { exportToDCTM } = require("./shared");
    exportToDCTM(formData, "test-cases", "Test Cases", user?.email || '');
  };

  const handleImportDCTM = async (file) => {
    const { importFromDCTM, validateDCTMTemplate } = require("./shared");
    const result = await importFromDCTM(file);
    
    if (!result.success) {
      alert(result.error);
      return;
    }
    
    // Validate template compatibility
    const validation = validateDCTMTemplate("test-cases", result.meta);
    if (validation.warning) {
      const proceed = window.confirm(`Warning: ${validation.warning}

Do you want to continue importing?`);
      if (!proceed) return;
    }
    
    // Increment version for imported data
    const currentVersion = parseFloat(result.data.version) || 1.0;
    const newVersion = (currentVersion + 0.1).toFixed(1);
    
    // Set form data with incremented version
    setFormData({ ...result.data, version: newVersion });
    setSavedTemplateId(null);
    
    alert(`Document imported successfully!
Original version: ${result.meta.documentVersion}
New version: ${newVersion}`);
  };


  const handleDownload = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setLoading(true);
    try {
      const response = await axios.post(`${API}/generate/test-cases`, { ...formData, created_by_email: user?.email || "" }, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `Test_Cases_${formData.project_name.replace(/\s+/g, '_') || 'Document'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) { console.error("Error:", error); alert("Error generating document."); }
    setLoading(false);
  };

  const handleDownloadPDF = async () => {
    // Save draft before downloading
    await saveDraftInternal(formData, savedTemplateId, false);
    setPdfLoading(true);
    try {
      await downloadTemplatePDF({
        title: formData.title || formData.project_name || formData.meeting_title || formData.company_name || "Test Cases",
        formData,
        settings: activeSettings || {},
        templateType: "Test Cases",
        templateId: "test-cases",
        createdBy: user?.email,
        selectedSetting: activeSettings?.id,
        allSettings: null
      });
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert(error.message || "Error generating PDF. Please try again.");
    }
    setPdfLoading(false);
  };

  const addTestCase = () => {
    const newId = `TC-${String(formData.test_cases.length + 1).padStart(3, '0')}`;
    setFormData({
      ...formData,
      test_cases: [...formData.test_cases, { tc_id: newId, title: '', description: '', preconditions: '', test_steps: '', expected_result: '', actual_result: '', test_data: '', priority: 'Medium', type: 'Functional', status: 'Not Run', executed_by: '', execution_date: '', notes: '' }]
    });
  };

  const updateTestCase = (index, field, value) => {
    const cases = [...formData.test_cases];
    cases[index] = { ...cases[index], [field]: value };
    setFormData({ ...formData, test_cases: cases });
  };

  const removeTestCase = (index) => {
    setFormData({ ...formData, test_cases: formData.test_cases.filter((_, i) => i !== index) });
  };

  const priorities = ['Critical', 'High', 'Medium', 'Low'];
  const types = ['Functional', 'Negative', 'Integration', 'Security', 'Performance', 'Usability', 'Regression'];
  const statuses = ['Not Run', 'Pass', 'Fail', 'Blocked', 'Skipped', 'In Progress'];

  const getStatusColor = (status) => {
    const colors = { 'Pass': 'bg-green-100', 'Fail': 'bg-red-100', 'Blocked': 'bg-yellow-100', 'In Progress': 'bg-blue-100', 'Skipped': 'bg-gray-100' };
    return colors[status] || 'bg-gray-50';
  };

  const passCount = formData.test_cases.filter(tc => tc.status === 'Pass').length;
  const failCount = formData.test_cases.filter(tc => tc.status === 'Fail').length;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/")} data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></Button>
              <div><h1 className="text-xl font-bold">Test Cases</h1><p className="text-sm text-gray-500">Test case documentation</p></div>
            </div>
            <div className="flex gap-2 items-center flex-wrap justify-end">
              <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Auto-save</span>
                <Switch checked={autoSave} onCheckedChange={setAutoSave} data-testid="auto-save-toggle" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setFormData(getSampleData())} data-testid="fill-sample-btn"><Sparkles className="h-4 w-4 mr-2" /> Sample</Button>
              <Button variant="outline" size="sm" onClick={() => { setFormData(getEmptyForm()); setSavedTemplateId(null); }} data-testid="reset-btn"><RotateCcw className="h-4 w-4 mr-2" /> Reset</Button>
            </div>
          </div>
          {lastSaved && (
            <div className="text-xs text-gray-500 mt-2 text-right flex items-center justify-end gap-3">
              {formData.version && <span className="bg-gray-100 px-2 py-0.5 rounded">v{formData.version}</span>}
              <span>Last saved: {lastSaved.toLocaleTimeString()}</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle>Test Cases</CardTitle>
            <div className="flex gap-4 mt-2">
              <div className="bg-green-100 rounded-lg px-3 py-1"><span className="text-xs text-gray-600">Pass:</span> <span className="font-bold text-green-700">{passCount}</span></div>
              <div className="bg-red-100 rounded-lg px-3 py-1"><span className="text-xs text-gray-600">Fail:</span> <span className="font-bold text-red-700">{failCount}</span></div>
              <div className="bg-gray-100 rounded-lg px-3 py-1"><span className="text-xs text-gray-600">Total:</span> <span className="font-bold">{formData.test_cases.length}</span></div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="col-span-2"><Label>Project Name *</Label><Input value={formData.project_name} onChange={(e) => setFormData({...formData, project_name: e.target.value})} placeholder="Project name" /></div>
              <div><Label>Module</Label><Input value={formData.module} onChange={(e) => setFormData({...formData, module: e.target.value})} placeholder="Module" /></div>
              <div><Label>Version</Label><Input value={formData.version} onChange={(e) => setFormData({...formData, version: e.target.value})} placeholder="2.0" /></div>
              <div><Label>Date</Label><Input type="date" value={formData.date} onChange={(e) => setFormData({...formData, date: e.target.value})} /></div>
            </div>

            <Separator />

            <div>
              <Label className="font-semibold text-lg">Test Cases ({formData.test_cases.length})</Label>
              <div className="space-y-4 mt-3">
                {formData.test_cases.map((tc, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border ${getStatusColor(tc.status)}`}>
                    <div className="flex justify-between items-start mb-3">
                      <span className="text-sm font-mono bg-white px-2 py-1 rounded">{tc.tc_id}</span>
                      <Button variant="ghost" size="icon" onClick={() => removeTestCase(idx)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="md:col-span-2"><Label className="text-xs">Title *</Label><Input value={tc.title} onChange={(e) => updateTestCase(idx, 'title', e.target.value)} placeholder="Test case title" className="text-sm" /></div>
                      <div><Label className="text-xs">Test Steps</Label><Textarea value={tc.test_steps} onChange={(e) => updateTestCase(idx, 'test_steps', e.target.value)} placeholder="1. Step one\n2. Step two" rows={3} className="text-sm" /></div>
                      <div><Label className="text-xs">Expected Result</Label><Textarea value={tc.expected_result} onChange={(e) => updateTestCase(idx, 'expected_result', e.target.value)} placeholder="Expected outcome" rows={3} className="text-sm" /></div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
                      <div><Label className="text-xs">Priority</Label>
                        <Select value={tc.priority} onValueChange={(v) => updateTestCase(idx, 'priority', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{priorities.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Type</Label>
                        <Select value={tc.type} onValueChange={(v) => updateTestCase(idx, 'type', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Status</Label>
                        <Select value={tc.status} onValueChange={(v) => updateTestCase(idx, 'status', v)}>
                          <SelectTrigger className="text-sm h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{statuses.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div><Label className="text-xs">Executed By</Label><Input value={tc.executed_by} onChange={(e) => updateTestCase(idx, 'executed_by', e.target.value)} placeholder="Name" className="text-sm" /></div>
                      <div><Label className="text-xs">Execution Date</Label><Input type="date" value={tc.execution_date} onChange={(e) => updateTestCase(idx, 'execution_date', e.target.value)} className="text-sm" /></div>
                    </div>
                  </div>
                ))}
              </div>
              <Button variant="outline" size="sm" onClick={addTestCase} className="mt-3"><Plus className="h-4 w-4 mr-1" /> Add Test Case</Button>
            </div>

            <Separator />
            <RAISection formData={formData} setFormData={setFormData} />
            <FollowupsSection formData={formData} setFormData={setFormData} />

            <div className="flex flex-col gap-3">
              <div className="flex gap-3">
                <Button onClick={() => handleSaveDraft(false)} disabled={saving || loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="save-draft-btn">
                <Save className="h-5 w-5 mr-2" /> {saving ? "Saving..." : (savedTemplateId ? "Update" : "Draft")}
              </Button>
              <Button onClick={handleDownload} disabled={loading || pdfLoading} className="flex-1" size="lg" data-testid="download-btn">
                <Download className="h-5 w-5 mr-2" /> {loading ? "Generating..." : "Download XLSX"}
            </Button>
              <Button onClick={handleDownloadPDF} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="download-pdf-btn">
                  <FileText className="h-5 w-5 mr-2" /> {pdfLoading ? "Generating..." : "PDF"}
              </Button>
              <input
                type="file"
                ref={dctmFileInputRef}
                accept=".dctm"
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    handleImportDCTM(e.target.files[0]);
                    e.target.value = '';
                  }
                }}
                className="hidden"
                data-testid="dctm-file-input"
              />
              <Button onClick={() => dctmFileInputRef.current?.click()} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="import-dctm-btn">
                <FileUp className="h-5 w-5 mr-2" /> Import
              </Button>
              <Button onClick={handleExportDCTM} disabled={loading || pdfLoading} variant="outline" className="flex-1" size="lg" data-testid="export-dctm-btn">
                <FileDown className="h-5 w-5 mr-2" /> Export
              </Button>
              </div>
              {(loading || pdfLoading) && (
                <div className="flex items-center justify-center gap-2 py-2 px-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                  <span className="text-sm text-blue-700">
                    {loading ? "Generating DOCX document..." : "Generating PDF document..."}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default TestCasesForm;
